import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../../../../constants/dataAids';
import getStyles from '../../ProductDetail/getStyles';
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import CommonDetails from '../../ProductDetail/CommonDetails';
import CheckoutButtons from '../details/CheckoutButtons';
import {
  RouteHandler,
  ShopViewConstants,
  ScrollWidgetActions
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import ProductLabels from '../details/ProductLabels';
import * as Metrics from '../../utils/trackMetric';
import * as MetricEvents from '../../../../constants/metrics';

function QuickViewLayout(props) {
  const {
    Grid,
    Grid: { Cell }
  } = UX2.Component;
  const ref = useRef(null);
  const [imageWidth, setImageWidth] = useState(0);
  useEffect(() => {
    setImageWidth(ref.current.offsetWidth);
  }, []);

  const { Block } = UX2.Element;
  const { quickViewWidth, translate = () => {} } = props;
  const modalContentWidth = quickViewWidth - 64;
  const styles = getStyles(props);
  const quickViewStyles = {
    ...styles,
    layoutWrapper: {
      position: 'relative',
      width: `${modalContentWidth}px`
    },
    labelsContainer: {
      'marginBottom': 0,
      '@xs': {
        display: 'block'
      },
      '@sm': {
        display: 'none'
      }
    },
    name: {
      'marginBottom': 0,
      'textAlign': 'center',
      '@sm': {
        textAlign: 'left'
      }
    },
    smartRibbon: {
      label: {
        marginBottom: 0
      }
    },
    imageWrapper: {
      ['li']: {
        width: `${imageWidth}px`
      }
    }
  };

  const handleFullDetailsClick = () => {
    Metrics.track(MetricEvents.QUICK_VIEW_MODAL_VIEW_FULL_DETAILS_CLICK);

    RouteHandler.navigate(ShopViewConstants.PRODUCT_DETAIL, {
      slug: props.product.slug
    });
    ScrollWidgetActions.scrollShopWidget();
  };

  const imageList = props.product?.assets.reduce((result, asset) => {
    if (asset.type === 'image') {
      result.push(asset.large_url);
    }
    return result;
  }, []);

  const noProductImage = OlsConfigStore.getState().noProductImage;

  return (
    <UX2.Element.Container.Fluid style={ quickViewStyles.layoutWrapper }>
      <Grid size={ 12 } bottom={ false } spacingSm='large'>
        <Cell size={ 6 }>
          <Block style={ quickViewStyles.imageWrapper }>
            <div ref={ ref }>
              { (imageList || noProductImage) && (
                <UX2.Component.CommerceCardCarousel
                  width={ imageWidth }
                  imageShape={ props.pdpImageShape }
                  imageList={ imageList.length > 0 ? imageList : [noProductImage] }
                  imageCropMethod={ props.pdpImageCropMethod }
                  areControlsShown={ imageList.length > 1 }
                  draggable={ false }
                />
              ) }
            </div>
          </Block>
        </Cell>
        <Cell size={ 6 } style={{ alignSelf: 'flex-start' }}>
          <Block style={{ marginBottom: 'medium' }}>
            <Grid size={ 4 } bottom={ false }>
              <Cell style={{ minWidth: '100%' }} size={ 4 }>
                <UX2.Element.Heading.Middle
                  data-aid={ dataAids.PRODUCT_NAME_RENDERED }
                  tag='h1'
                  children={ props.product.name }
                  style={ quickViewStyles.name }
                  group='Group'
                  featured
                />
                { (props.product.ship_free ||
                  props.product.limited_edition ||
                  props.product.free_return) && (
                  <Block style={ quickViewStyles.labelsContainer }>
                    <ProductLabels product={ props.product } translate={ translate } />
                  </Block>
                ) }
                <CommonDetails { ...props } showProductLabels isQuickView={ props.isQuickView } />
                <Block style={{ marginTop: 'medium' }}>
                  <CheckoutButtons
                    renderMode={ props.renderMode }
                    category={ props.category }
                    prices={ props.determinePrice() }
                    { ...props.baseState }
                    requiredFieldsBlank={ props.requiredFieldsBlank }
                    isSelectedVariantInStock={ props.isSelectedVariantInStock }
                    handleAddToCart={ props.handleAddToCart }
                    handleApplePayClick={ props.handleApplePayClick }
                    getOrderData={ props.getVariantData }
                    phoneRequired={ props.stripeConfig.require_phone_number }
                    isPaymentRequestEnabled={ props.stripeConfig.with_payment_request }
                    isPoyntPaymentRequestEnabled={ props.poyntConfig.with_apple_pay }
                    translate={ translate }
                    hasAddedToCartLabel={ props.hasAddedToCartLabel }
                    isQuickView={ props.isQuickView }
                  />
                </Block>
              </Cell>
            </Grid>
            <UX2.Element.MoreLink
              icon='arrowRight'
              right
              iconSize='1.2em'
              children={ translate('products.view_full_details') }
              onClick={ handleFullDetailsClick }
            />
          </Block>
        </Cell>
      </Grid>
    </UX2.Element.Container.Fluid>
  );
}

QuickViewLayout.propTypes = {
  baseState: PropTypes.object,
  isQuickView: PropTypes.bool,
  quickViewWidth: PropTypes.number,
  determineAddOnCharges: PropTypes.func,
  determinePrice: PropTypes.func,
  isSelectedVariantInStock: PropTypes.bool,
  product: PropTypes.object,
  renderMode: PropTypes.string,
  requiredFieldsBlank: PropTypes.bool,
  translate: PropTypes.func,
  assetChangedByVariantSelection: PropTypes.bool,
  category: PropTypes.string,
  currentAssetIndex: PropTypes.number,
  fetching: PropTypes.bool,
  processing: PropTypes.bool,
  inSession: PropTypes.bool,
  handleAddToCart: PropTypes.func,
  handleApplePayClick: PropTypes.func,
  getVariantData: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  pdpImageCropMethod: PropTypes.string,
  pdpImageShape: PropTypes.string,
  stripeConfig: PropTypes.object,
  poyntConfig: PropTypes.object,
  pdpLayout: PropTypes.string.isRequired,
  hasAddedToCartLabel: PropTypes.bool
};

export default QuickViewLayout;
